/**
 * useNotes Hook
 *
 * Hook for managing notes related to a contact in inbox-v3.
 * Fetches notes timeline and allows creating new manual notes.
 */

import {useCallback, useMemo} from 'react'
import moment from 'moment'
import {
  useGetNotesTimelineByPhoneNumberQuery,
  useCreateNoteByPhoneNumberMutation,
} from '../../../../../services/cellcast.v2'
import {
  NoteTimelineItem,
  NoteType,
  NoteManualContent,
  NoteVoiceContent,
} from '../../../../../services/cellcast.v2.types'

export interface UseNotesOptions {
  /** Phone number to fetch notes for */
  phoneNumber?: string
  /** Whether to enable the query */
  enabled?: boolean
  /** Page number for pagination */
  page?: number
  /** Limit for pagination */
  limit?: number
}

export interface GroupedNote {
  /** Raw date string (YYYY-MM-DD) */
  date: string
  /** Human-readable date label (Today, Yesterday, Mar 28, 2026, etc.) */
  dateLabel: string
  /** Note items for this date */
  items: NoteTimelineItem[]
}

export interface NoteIconConfig {
  icon: string
  className: string
  color: string
}

// Map note types to icons and styles
export const NOTE_TYPE_ICONS: Record<NoteType, NoteIconConfig> = {
  manual: {
    icon: 'bi-journal-text',
    className: 'note-manual',
    color: '#7239ea',
  },
  voice: {
    icon: 'bi-mic-fill',
    className: 'note-voice',
    color: '#009ef7',
  },
}

export interface UseNotesReturn {
  /** Grouped notes by date */
  groupedNotes: GroupedNote[]
  /** Raw note items */
  notes: NoteTimelineItem[]
  /** Whether notes are loading */
  isLoading: boolean
  /** Whether notes are being fetched */
  isFetching: boolean
  /** Error message if any */
  error: string | null
  /** Total count of notes */
  total: number
  /** Create a new manual note */
  createNote: (noteText: string) => Promise<boolean>
  /** Whether a note operation is in progress */
  isSubmitting: boolean
  /** Refetch notes */
  refetch: () => void
  /** Get icon config for a note type */
  getNoteIcon: (type: NoteType) => NoteIconConfig
  /** Get note text content */
  getNoteText: (note: NoteTimelineItem) => string
  /** Check if note has voice content */
  isVoiceNote: (note: NoteTimelineItem) => note is NoteTimelineItem & {content: NoteVoiceContent}
}

/**
 * Format date to human-readable label
 */
const formatDateLabel = (dateString: string): string => {
  const date = moment(dateString)
  const today = moment().startOf('day')
  const yesterday = moment().subtract(1, 'day').startOf('day')

  if (date.isSame(today, 'day')) {
    return 'Today'
  }
  if (date.isSame(yesterday, 'day')) {
    return 'Yesterday'
  }
  // Show day of week for last 7 days
  if (date.isAfter(moment().subtract(7, 'days'))) {
    return date.format('dddd') // e.g., "Monday"
  }
  // Show full date for older items
  return date.format('MMM D, YYYY') // e.g., "Mar 28, 2026"
}

/**
 * Group notes by date
 */
const groupNotesByDate = (notes: NoteTimelineItem[]): GroupedNote[] => {
  const grouped: Record<string, NoteTimelineItem[]> = {}

  notes.forEach((note) => {
    const date = moment(note.createdAt).format('YYYY-MM-DD')
    if (!grouped[date]) {
      grouped[date] = []
    }
    grouped[date].push(note)
  })

  // Convert to array and sort by date (newest first)
  return Object.entries(grouped)
    .map(([date, items]) => ({
      date,
      dateLabel: formatDateLabel(date),
      items: items.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    }))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

export function useNotes(options: UseNotesOptions = {}): UseNotesReturn {
  const {phoneNumber, enabled = true, page = 1, limit = 20} = options

  // Clean phone number - remove country code prefix if present
  const cleanPhoneNumber = useMemo(() => {
    if (!phoneNumber) return ''
    // Remove leading + or country codes like 61
    return phoneNumber.replace(/^\+?61/, '').replace(/\D/g, '')
  }, [phoneNumber])

  // Fetch notes timeline
  const {
    currentData,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useGetNotesTimelineByPhoneNumberQuery(
    {
      phoneNumber: cleanPhoneNumber,
      page,
      limit,
    },
    {
      skip: !enabled || !cleanPhoneNumber,
      refetchOnMountOrArgChange: true,
    }
  )

  // Create note mutation
  const [createNoteMutation, {isLoading: isCreating}] = useCreateNoteByPhoneNumberMutation()

  // Extract notes from response - use currentData to avoid showing stale data from previous contact
  // currentData is undefined while loading for new args, which prevents showing old contact's notes
  const notes = useMemo(() => {
    if (!currentData?.data?.items) return []
    return currentData.data.items.map((item) => ({
      ...item,
      // Ensure id is always set
      id: item.id || item._id,
    }))
  }, [currentData])

  // Group notes by date
  const groupedNotes = useMemo(() => {
    return groupNotesByDate(notes)
  }, [notes])

  // Error handling
  const error = useMemo(() => {
    if (queryError) {
      return 'Failed to load notes'
    }
    return null
  }, [queryError])

  // Total count
  const total = currentData?.data?.total || 0

  // Create note
  const createNote = useCallback(
    async (noteText: string): Promise<boolean> => {
      if (!cleanPhoneNumber || !noteText.trim()) {
        return false
      }
      try {
        const result = await createNoteMutation({
          phoneNumber: cleanPhoneNumber,
          noteText: noteText.trim(),
        }).unwrap()
        // API may return 'success' or 'status' for success indicator
        const isSuccess = (result as any).success || (result as any).status
        if (isSuccess) {
          refetch()
          return true
        }
        return false
      } catch (err) {
        console.error('Failed to create note:', err)
        return false
      }
    },
    [createNoteMutation, cleanPhoneNumber, refetch]
  )

  // Get icon config for note type
  const getNoteIcon = (type: NoteType): NoteIconConfig => {
    return NOTE_TYPE_ICONS[type] || NOTE_TYPE_ICONS.manual
  }

  // Type guard for voice note
  const isVoiceNote = (
    note: NoteTimelineItem
  ): note is NoteTimelineItem & {content: NoteVoiceContent} => {
    return note.type === 'voice' && 'summary' in note.content
  }

  // Get note text content
  const getNoteText = (note: NoteTimelineItem): string => {
    if (note.type === 'manual') {
      return (note.content as NoteManualContent).text || ''
    }
    if (note.type === 'voice') {
      const voiceContent = note.content as NoteVoiceContent
      return voiceContent.summary || voiceContent.transcriptExcerpt || ''
    }
    return ''
  }

  return {
    groupedNotes,
    notes,
    isLoading,
    isFetching,
    error,
    total,
    createNote,
    isSubmitting: isCreating,
    refetch,
    getNoteIcon,
    getNoteText,
    isVoiceNote,
  }
}

export default useNotes